import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { TranslatePipe } from '@ngx-translate/core'
import { MenuModule } from 'primeng/menu'
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu'
import { Nullable } from 'primeng/ts-helpers'

import { DeviceService } from '@ti/core/common/services'

import { LanguageService } from '../../../i18n'
import { DynamicHostComponent } from '../../components/dynamic-host/dynamic-host.component'
import { MenuAppendTo, OptionsItem } from '../../contracts/dropdown'
import { LifecycleDirective } from '../../directives/lifecycle.directive'
import { TooltipDirective } from '../../directives/tooltip.directive'

type TieredMenuOption = OptionsItem & { hint?: string; items?: TieredMenuOption[] }

// p-tieredMenu shows `tooltip` itself, untranslated, beside the tooltip the item template renders.
const toTieredMenuOption = ({ tooltip, items, ...option }: OptionsItem): TieredMenuOption => ({
  ...option,
  hint: tooltip,
  items: items?.map(toTieredMenuOption),
})

const START_OPTION: TieredMenuOption = {
  disabled: true,
  isStart: true,
  styleClass: 'menu-start-item',
}

@Component({
  selector: 'app-tiered-menu',
  templateUrl: 'tiered-menu.component.html',
  styleUrls: ['tiered-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DynamicHostComponent,
    MenuModule,
    NgTemplateOutlet,
    TieredMenuModule,
    LifecycleDirective,
    TooltipDirective,
    TranslatePipe,
  ],
})
export class TieredMenuComponent {
  public readonly options = input<Nullable<OptionsItem[]>>()
  public readonly appendTo = input<Nullable<MenuAppendTo>>()
  public readonly disableSubOptionsWrap = input<boolean>(true)
  public readonly panelWidth = input<Nullable<string>>('mw-170')
  public readonly startText = input<Nullable<string>>() // Isn't supported by tiered-menu
  public readonly startTemplate = input<Nullable<TemplateRef<unknown>>>()

  protected readonly device = inject(DeviceService)
  protected readonly languageService = inject(LanguageService)

  protected readonly selectedParentOption = signal<Nullable<OptionsItem>>(undefined)
  protected readonly labels = toSignal(this.languageService.massTranslate$({ back: 'button.back' }))
  protected readonly mobileOptions = computed(() => {
    const labels = this.labels()
    const options = this.options()
    const selectedParentOption = this.selectedParentOption()

    return selectedParentOption
      ? [
          {
            label: labels?.back,
            icon: 'pi pi-angle-left',
            styleClass: 'has-bottom-border',
            backButton: true,
          },
          ...selectedParentOption.submenuItems,
        ]
      : options?.map((option) => {
          return {
            ...option,
            items: undefined,
            hasSubmenu: option?.items?.length,
            submenuItems: option?.items,
          }
        })
  })

  protected readonly tieredOptions = computed(() => {
    const options = this.options()?.map(toTieredMenuOption)
    return options?.length && this.startTemplate() ? [START_OPTION, ...options] : options
  })

  protected readonly standardMenuStyleClass = computed(() => {
    // Dynamic panel width can cause unpredictable panel-width behavior
    // when a sub options panel wider than a parent options panel
    const base = 'menu-primary p-4 br-6'
    const width = this.panelWidth()
    return `${base} ${width}`.trim()
  })

  protected readonly tieredMenuStyleClass = computed(() => {
    const base = 'menu-primary p-4 br-6'
    const width = this.panelWidth()
    const noSubOptionsWrap = this.disableSubOptionsWrap() ? 'no-sub-option-wrap' : ''
    return `${base} ${width} ${noSubOptionsWrap}`.trim()
  })

  @ViewChild('menu')
  protected readonly menu!: TieredMenu

  public get visible() {
    return this.menu?.visible
  }

  public toggle(event: MouseEvent) {
    if (this.menu.visible) {
      return this.menu.hide(event)
    }
    if (this.options()?.length) {
      return this.menu.toggle(event)
    }
  }

  protected mobileOptionClicked(option: OptionsItem, event: Event) {
    if (option.hasSubmenu || option.backButton) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (option.hasSubmenu) {
      this.selectedParentOption.set(option)
    } else {
      // Postpone the change to allow executing option command
      setTimeout(() => this.selectedParentOption.set(undefined), 32)
    }
  }
}
